import { Button } from "@/components/ui/button";
import { useLocale, useT } from "@/i18n/locale-context";
import { cn } from "@/lib/utils";
import { useAnalytics } from "@/modules/analytics";

interface ConsentBannerProps {
  className?: string;
}

export function ConsentBanner({
  className,
}: ConsentBannerProps): JSX.Element | null {
  const { adapters, consent, setConsent } = useAnalytics();
  const t = useT();
  const { localePath } = useLocale();

  const requiresConsent = adapters.some((adapter) => adapter.requiresConsent);
  if (!requiresConsent || consent !== "pending") return null;

  return (
    <aside
      aria-label={t("consent.title")}
      className={cn(
        "fixed inset-x-4 bottom-4 z-50 rounded-2xl border border-border bg-surface/95 p-4 shadow-lg backdrop-blur",
        "md:inset-x-auto md:left-6 md:right-6 md:bottom-6 md:mx-auto md:max-w-2xl",
        className,
      )}
      role="dialog"
    >
      <div className="space-y-3">
        <div className="space-y-1">
          <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">
            {t("consent.title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("consent.description")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => {
              setConsent("granted");
            }}
            size="compact"
            type="button"
          >
            {t("consent.accept")}
          </Button>
          <Button
            onClick={() => {
              setConsent("denied");
            }}
            size="compact"
            type="button"
            variant="outline"
          >
            {t("consent.decline")}
          </Button>
          <a
            className="inline-flex items-center text-sm font-medium text-primary underline-offset-4 hover:underline"
            href={localePath("/legal")}
          >
            {t("consent.learnMore")}
          </a>
        </div>
      </div>
    </aside>
  );
}
